import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { IndianRupee, FileText, Plus, X, Pencil, Trash2, Zap } from 'lucide-react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeeManagement() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('overview'); // overview, structures, invoices
  const [stats, setStats] = useState(null);
  const [structures, setStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [invTotal, setInvTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth, setFilterMonth] = useState('2026-03');
  const [students, setStudents] = useState([]);
  const [additionalForm, setAdditionalForm] = useState({ charge_name: '', amount: '', target: 'class', class_id: '', student_id: '', month: new Date().toISOString().slice(0, 7) });

  const [form, setForm] = useState({ class_id: '', categories: [{ name: 'Tuition', amount: 5000, frequency: 'monthly' }] });
  const [editStructureId, setEditStructureId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchAll = async () => {
    try {
      const [statsRes, structRes, classRes, studRes] = await Promise.all([
        axios.get(`${API}/fees/stats?month=${filterMonth}`, { headers }),
        axios.get(`${API}/fees/structure`, { headers }),
        axios.get(`${API}/classes`, { headers }),
        axios.get(`${API}/students`, { headers }),
      ]);
      setStats(statsRes.data.data);
      setStructures(structRes.data.data || []);
      setClasses(classRes.data.data || []);
      setStudents(studRes.data.data?.students || []);
    } catch {}
  };

  const fetchInvoices = async () => {
    const params = new URLSearchParams({ page: 1, limit: 50 });
    if (filterStatus) params.set('status', filterStatus);
    if (filterMonth) params.set('month', filterMonth);
    try {
      const res = await axios.get(`${API}/fees/invoices?${params}`, { headers });
      setInvoices(res.data.data.invoices || []);
      setInvTotal(res.data.data.total || 0);
    } catch {}
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (tab === 'invoices') fetchInvoices(); }, [tab, filterStatus, filterMonth]);

  const addCategory = () => setForm({ ...form, categories: [...form.categories, { name: '', amount: 0, frequency: 'monthly' }] });
  const removeCategory = (i) => setForm({ ...form, categories: form.categories.filter((_, idx) => idx !== i) });
  const updateCategory = (i, field, val) => {
    const cats = [...form.categories];
    cats[i] = { ...cats[i], [field]: field === 'amount' ? parseFloat(val) || 0 : val };
    setForm({ ...form, categories: cats });
  };

  const handleCreateStructure = async (e) => {
    e.preventDefault();
    try {
      if (editStructureId) {
        await axios.put(`${API}/fees/structure/${editStructureId}`, form, { headers });
        toast.success('Fee structure updated');
      } else {
        await axios.post(`${API}/fees/structure`, form, { headers });
        toast.success('Fee structure created');
      }
      setShowForm(false);
      setEditStructureId(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEditStructure = (s) => {
    setForm({ class_id: s.class_id, categories: s.categories });
    setEditStructureId(s.structure_id);
    setShowForm(true);
  };

  const handleDeleteStructure = (s) => {
    setConfirm({
      title: 'Delete Fee Structure',
      message: `Delete fee structure for ${s.class_name} ${s.section}? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/fees/structure/${s.structure_id}`, { headers });
          toast.success('Fee structure deleted');
          fetchAll();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
      }
    });
  };

  const handleGenerate = async (classId) => {
    try {
      const params = classId ? `?class_id=${classId}&month=${filterMonth}` : `?month=${filterMonth}`;
      const res = await axios.post(`${API}/fees/generate-invoices${params}`, {}, { headers });
      toast.success(res.data.message);
      fetchInvoices();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddCharge = async () => {
    if (!additionalForm.charge_name.trim() || !additionalForm.amount) { toast.error('Enter charge name and amount'); return; }
    if (additionalForm.target === 'class' && !additionalForm.class_id) { toast.error('Select a class'); return; }
    if (additionalForm.target === 'student' && !additionalForm.student_id) { toast.error('Select a student'); return; }
    try {
      const payload = {
        charge_name: additionalForm.charge_name,
        amount: parseFloat(additionalForm.amount),
        month: additionalForm.month,
        class_id: additionalForm.target === 'class' ? additionalForm.class_id : null,
        student_id: additionalForm.target === 'student' ? additionalForm.student_id : null,
      };
      const res = await axios.post(`${API}/fees/invoices/additional`, payload, { headers });
      toast.success(res.data.message);
      setShowAdditional(false);
      setAdditionalForm({ charge_name: '', amount: '', target: 'class', class_id: '', student_id: '', month: new Date().toISOString().slice(0, 7) });
      fetchInvoices();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const statusColors = { pending: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', partial: 'bg-blue-100 text-blue-700', overdue: 'bg-red-100 text-red-700' };
  const FEE_NAMES = ['Tuition', 'Transport', 'Lab Fee', 'Library', 'Sports', 'Computer Lab', 'Activity Fee', 'Admission Fee', 'Exam Fee', 'Other'];

  return (
    <div className="space-y-6" data-testid="fee-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="fee-management-title">Fee Management</h1>
          <p className="text-sm text-slate-500 mt-1">Configure fee structures, generate invoices, track payments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAdditional(true)}><Zap className="h-4 w-4 mr-2" /> Additional Charge</Button>
          <Button variant="outline" onClick={() => setShowGenerate(true)} data-testid="generate-invoices-btn"><FileText className="h-4 w-4 mr-2" /> Generate Invoices</Button>
          <Button onClick={() => { setShowForm(true); setEditStructureId(null); setForm({ class_id: '', categories: [{ name: 'Tuition', amount: 5000, frequency: 'monthly' }] }); }} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="add-fee-structure-btn"><Plus className="h-4 w-4 mr-2" /> Fee Structure</Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Total Invoices</p><p className="text-2xl font-bold text-slate-900">{stats.total_invoices}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Total Amount</p><p className="text-2xl font-bold text-slate-900 flex items-center"><IndianRupee className="h-5 w-5" />{(stats.total_amount || 0).toLocaleString()}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Collected</p><p className="text-2xl font-bold text-emerald-600 flex items-center"><IndianRupee className="h-5 w-5" />{(stats.collected || 0).toLocaleString()}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-400">Defaulters</p><p className="text-2xl font-bold text-red-600">{stats.defaulter_count || 0}</p></div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {['overview', 'structures', 'invoices'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium rounded-md capitalize ${tab === t ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {/* Structures Tab */}
      {tab === 'structures' && (
        <div className="space-y-4 mt-6">
          {structures.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200"><p className="text-slate-400">No fee structures defined yet</p></div>
          ) : structures.map(s => (
            <div key={s.structure_id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`structure-${s.structure_id}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">{s.class_name} {s.section}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{s.academic_year}</span>
                  <button onClick={() => handleEditStructure(s)} className="p-1.5 rounded-lg hover:bg-slate-100" title="Edit structure">
                    <Pencil className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <button onClick={() => handleDeleteStructure(s)} className="p-1.5 rounded-lg hover:bg-red-50" title="Delete structure">
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(s.categories || []).map((c, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-400">{c.name}</p>
                    <p className="text-sm font-bold text-slate-900 flex items-center justify-center"><IndianRupee className="h-3 w-3" />{c.amount.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invoices Tab */}
      {tab === 'invoices' && (
        <div className="mt-6">
          <div className="flex gap-2 mb-4">
            <Select value={filterMonth.split('-')[1]} onValueChange={(m) => setFilterMonth(`${filterMonth.split('-')[0]}-${m}`)}>
              <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
                  <SelectItem key={m} value={m}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterMonth.split('-')[0]} onValueChange={(y) => setFilterMonth(`${y}-${filterMonth.split('-')[1]}`)}>
              <SelectTrigger className="w-24 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['2024','2025','2026','2027'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-36 h-9"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {invoices.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200"><p className="text-slate-400">No invoices found. Generate invoices first.</p></div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
              <table className="w-full" data-testid="invoices-table">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Student</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Class</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Month</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500">Amount</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500">Paid</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {invoices.map(inv => (
                    <tr key={inv.invoice_id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{inv.student_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{inv.class_name} {inv.section}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{inv.month}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-mono">{inv.net_amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right font-mono text-emerald-600">{(inv.paid_amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-center"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[inv.status] || ''}`}>{inv.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="text-center py-12 mt-6 bg-slate-50 rounded-2xl border border-slate-200">
          <IndianRupee className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Fee Dashboard</p>
          <p className="text-sm text-slate-400 mt-1">Define fee structures per class, then generate monthly invoices</p>
          <div className="flex gap-2 justify-center mt-4">
            <Button variant="outline" onClick={() => setTab('structures')}>View Structures</Button>
            <Button variant="outline" onClick={() => setTab('invoices')}>View Invoices</Button>
          </div>
        </div>
      )}

      {/* Fee Structure Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fee-structure-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">{editStructureId ? 'Edit Fee Structure' : 'Create Fee Structure'}</h2><button onClick={() => { setShowForm(false); setEditStructureId(null); }}><X className="h-5 w-5 text-slate-400" /></button></div>
            <form onSubmit={handleCreateStructure} className="space-y-4">
              <div><Label className="text-xs text-slate-500">Class *</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between"><Label className="text-xs text-slate-500">Fee Categories</Label><button type="button" onClick={addCategory} className="text-xs text-teal-600 font-medium">+ Add Category</button></div>
                {form.categories.map((cat, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Select value={cat.name} onValueChange={(v) => updateCategory(i, 'name', v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>{FEE_NAMES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={cat.amount} onChange={(e) => updateCategory(i, 'amount', e.target.value)} className="w-28" placeholder="Amount" />
                    {form.categories.length > 1 && <button type="button" onClick={() => removeCategory(i)}><X className="h-4 w-4 text-red-400" /></button>}
                  </div>
                ))}
                <p className="text-right text-sm font-semibold text-slate-700">Total: {form.categories.reduce((s, c) => s + c.amount, 0).toLocaleString()}/mo</p>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="save-fee-structure-btn">{editStructureId ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Invoices Modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="generate-invoices-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Generate Invoices</h2><button onClick={() => setShowGenerate(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Month</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={filterMonth.split('-')[1]} onValueChange={(m) => setFilterMonth(`${filterMonth.split('-')[0]}-${m}`)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
                        <SelectItem key={m} value={m}>{['January','February','March','April','May','June','July','August','September','October','November','December'][i]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterMonth.split('-')[0]} onValueChange={(y) => setFilterMonth(`${y}-${filterMonth.split('-')[1]}`)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['2024','2025','2026','2027'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-sm text-slate-500">This will generate invoices for all students with a fee structure defined.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowGenerate(false)} className="flex-1">Cancel</Button>
                <Button onClick={() => { handleGenerate(null); setShowGenerate(false); }} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white" data-testid="confirm-generate-btn">Generate All</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Additional Charge Modal */}
      {showAdditional && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Add Additional Charge</h2>
              <button onClick={() => setShowAdditional(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Charge Name</Label>
                <Input value={additionalForm.charge_name} onChange={e => setAdditionalForm({ ...additionalForm, charge_name: e.target.value })} placeholder="e.g. Sports Fee, Trip Fee, Exam Fee" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Amount (₹)</Label>
                <Input type="number" value={additionalForm.amount} onChange={e => setAdditionalForm({ ...additionalForm, amount: e.target.value })} placeholder="200" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Apply To</Label>
                <Select value={additionalForm.target} onValueChange={v => setAdditionalForm({ ...additionalForm, target: v, class_id: '', student_id: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="class">Entire Class</SelectItem>
                    <SelectItem value="student">Individual Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {additionalForm.target === 'class' && (
                <div>
                  <Label className="text-xs text-slate-500">Class</Label>
                  <Select value={additionalForm.class_id} onValueChange={v => setAdditionalForm({ ...additionalForm, class_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name} {c.section}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {additionalForm.target === 'student' && (
                <div>
                  <Label className="text-xs text-slate-500">Student</Label>
                  <Select value={additionalForm.student_id} onValueChange={v => setAdditionalForm({ ...additionalForm, student_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map(s => <SelectItem key={s.student_id} value={s.student_id}>{s.full_name} ({s.admission_number})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Month</Label>
                <div className="flex gap-2 mt-1">
                  <Select value={additionalForm.month.split('-')[1]} onValueChange={(m) => setAdditionalForm({ ...additionalForm, month: `${additionalForm.month.split('-')[0]}-${m}` })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
                        <SelectItem key={m} value={m}>{['January','February','March','April','May','June','July','August','September','October','November','December'][i]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={additionalForm.month.split('-')[0]} onValueChange={(y) => setAdditionalForm({ ...additionalForm, month: `${y}-${additionalForm.month.split('-')[1]}` })}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['2024','2025','2026','2027'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAdditional(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleAddCharge} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white">Add Charge</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
